// src/UserMenu.js
//
// Avatar in the top-right of the shell header. Click toggles a menu showing who
// is signed in, with self-service account edits and logout. Modelled on the
// district/constituency profiles menus so the three apps feel the same.
import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import axios from "axios";

// "Ciaran McMonagle" -> "CM". Falls back to "U" when the account has no name.
const initials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0].toUpperCase())
    .slice(0, 2)
    .join("") || "U";

// DRF answers a rejected write with {field: ["msg", ...]} and a refused one with
// {detail: "msg"}. Flatten both into {field: "msg"} for rendering, keeping
// anything unrecognised under `_` so a failure is never silent.
const readErrors = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") {
    return { _: "Something went wrong. Please try again." };
  }
  const out = {};
  Object.entries(data).forEach(([key, value]) => {
    const field = key === "detail" || key === "non_field_errors" ? "_" : key;
    out[field] = Array.isArray(value) ? value.join(" ") : String(value);
  });
  return out;
};

const EMPTY_FORM = {
  name: "",
  email: "",
  current_password: "",
  new_password: "",
  confirm_password: "",
};

class UserMenu extends React.Component {
  state = {
    anchorEl: null,
    dialog: null, // null | "details" | "password"
    form: EMPTY_FORM,
    errors: {},
    saving: false,
    toast: "",
  };

  // Toggle rather than open, so a second click on the avatar closes the menu.
  handleAvatarClick = (event) => {
    if (this.state.anchorEl) {
      this.handleMenuClose();
    } else {
      this.setState({ anchorEl: event.currentTarget });
    }
  };

  handleMenuClose = () => this.setState({ anchorEl: null });

  openDetails = () => {
    const { user } = this.props;
    this.setState({
      dialog: "details",
      form: { ...EMPTY_FORM, name: user?.name || "", email: user?.email || "" },
      errors: {},
    });
  };

  openPassword = () =>
    this.setState({ dialog: "password", form: { ...EMPTY_FORM }, errors: {} });

  closeDialog = () => {
    // Leave it open while a request is in flight, so the result has somewhere to land.
    if (this.state.saving) return;
    this.setState({ dialog: null, form: EMPTY_FORM, errors: {} });
  };

  setField = (field) => (event) =>
    this.setState((prev) => ({ form: { ...prev.form, [field]: event.target.value } }));

  // The session cookie is cleared server-side; a full page load back to "/"
  // drops any React state rather than leaving a stale shell behind.
  handleLogout = () => {
    axios
      .post("/api/user/logout/")
      .then(() => {
        window.location.href = "/";
      })
      .catch((error) => {
        console.error("Logout failed:", error);
      });
  };

  saveDetails = (event) => {
    event.preventDefault();
    const { name, email } = this.state.form;
    this.setState({ saving: true, errors: {} });

    axios
      .patch("/api/user/details/", { name, email })
      .then((res) => {
        this.setState({
          saving: false,
          dialog: null,
          form: EMPTY_FORM,
          toast: "Details updated.",
        });
        // The header avatar and the access context both read this user, so let
        // the host re-read it rather than guessing at the new shape here.
        this.props.onUserUpdated?.(res.data);
      })
      .catch((error) => this.setState({ saving: false, errors: readErrors(error) }));
  };

  savePassword = (event) => {
    event.preventDefault();
    const { current_password, new_password, confirm_password } = this.state.form;

    if (new_password !== confirm_password) {
      this.setState({ errors: { confirm_password: "The two passwords do not match." } });
      return;
    }

    this.setState({ saving: true, errors: {} });
    axios
      .post("/api/user/change-password/", { current_password, new_password })
      .then(() =>
        this.setState({
          saving: false,
          dialog: null,
          form: EMPTY_FORM,
          toast: "Password updated.",
        })
      )
      .catch((error) => this.setState({ saving: false, errors: readErrors(error) }));
  };

  renderDetailsDialog() {
    const { dialog, form, errors, saving } = this.state;
    return (
      <Dialog open={dialog === "details"} onClose={this.closeDialog} maxWidth="xs" fullWidth>
        <form onSubmit={this.saveDetails}>
          <DialogTitle sx={{ fontWeight: 700 }}>Change user details</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {errors._ && <Alert severity="error">{errors._}</Alert>}
              <TextField
                label="Name"
                value={form.name}
                onChange={this.setField("name")}
                error={Boolean(errors.name)}
                helperText={errors.name || " "}
                size="small"
                autoFocus
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={this.setField("email")}
                error={Boolean(errors.email)}
                helperText={errors.email || "You sign in with this address."}
                size="small"
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={this.closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  }

  renderPasswordDialog() {
    const { dialog, form, errors, saving } = this.state;
    return (
      <Dialog open={dialog === "password"} onClose={this.closeDialog} maxWidth="xs" fullWidth>
        <form onSubmit={this.savePassword}>
          <DialogTitle sx={{ fontWeight: 700 }}>Change password</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {errors._ && <Alert severity="error">{errors._}</Alert>}
              <TextField
                label="Current password"
                type="password"
                autoComplete="current-password"
                value={form.current_password}
                onChange={this.setField("current_password")}
                error={Boolean(errors.current_password)}
                helperText={errors.current_password || " "}
                size="small"
                autoFocus
                fullWidth
              />
              <TextField
                label="New password"
                type="password"
                autoComplete="new-password"
                value={form.new_password}
                onChange={this.setField("new_password")}
                error={Boolean(errors.new_password)}
                helperText={errors.new_password || " "}
                size="small"
                fullWidth
              />
              <TextField
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                value={form.confirm_password}
                onChange={this.setField("confirm_password")}
                error={Boolean(errors.confirm_password)}
                helperText={errors.confirm_password || " "}
                size="small"
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={this.closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? "Saving..." : "Update password"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  }

  render() {
    const { anchorEl, toast } = this.state;
    const { user } = this.props;
    const isOpen = Boolean(anchorEl);

    return (
      <>
        <IconButton
          onClick={this.handleAvatarClick}
          size="small"
          aria-controls={isOpen ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? "true" : undefined}
          aria-label="Account menu"
          sx={{ ml: 1 }}
        >
          <Avatar
            sx={{
              width: 38,
              height: 38,
              bgcolor: "#0497b2",
              fontWeight: "bold",
              fontSize: 14,
              border: isOpen ? "2px solid #0497b2" : "2px solid transparent",
              transition: "border 0.2s ease",
            }}
          >
            {initials(user?.name)}
          </Avatar>
        </IconButton>

        <Menu
          id="account-menu"
          anchorEl={anchorEl}
          open={isOpen}
          onClose={this.handleMenuClose}
          onClick={this.handleMenuClose}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          slotProps={{
            paper: {
              elevation: 4,
              sx: {
                mt: 1.5,
                borderRadius: 4,
                minWidth: 220,
                overflow: "visible",
                // Little caret pointing back up at the avatar.
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 20,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            },
          }}
        >
          <MenuItem disabled sx={{ opacity: "1 !important" }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: "text.primary" }}>
                {user?.name || "User"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.company || user?.email || ""}
              </Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem onClick={this.openDetails} sx={{ py: 1.5 }}>
            <ManageAccountsOutlinedIcon fontSize="small" style={{ marginRight: 12 }} />
            Change user details
          </MenuItem>
          <MenuItem onClick={this.openPassword} sx={{ py: 1.5 }}>
            <LockOutlinedIcon fontSize="small" style={{ marginRight: 12 }} />
            Change password
          </MenuItem>
          <Divider />
          <MenuItem onClick={this.handleLogout} sx={{ py: 1.5 }}>
            <LogoutIcon fontSize="small" style={{ marginRight: 12 }} />
            Logout
          </MenuItem>
        </Menu>

        {this.renderDetailsDialog()}
        {this.renderPasswordDialog()}

        <Snackbar
          open={Boolean(toast)}
          autoHideDuration={4000}
          onClose={() => this.setState({ toast: "" })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="success" variant="filled" onClose={() => this.setState({ toast: "" })}>
            {toast}
          </Alert>
        </Snackbar>
      </>
    );
  }
}

export default UserMenu;
